import { List, Map } from "immutable";
import { ContextStore } from "@core/store/contextStore";
import { MappedStore } from "@core/store/mappedStore";
import { SimpleMappedStorePresentation } from "@core/store/mappedStorePresentation";
import { SimpleStore, Store } from "@core/store/store";
import { Page } from "@utils/page";
import { CurrentProfileService } from "@domain/currentProfile/currentProfileService";
import { GroupMemberRole } from "@domain/group/group";
import { GroupApi } from "@domain/group/groupApi";
import { GroupMember } from "@domain/group/groupMember";
import { GroupService } from "@domain/group/groupService";
import { QuizApi } from "@domain/lms/quiz/quizApi";
import { OrganizationMemberRole } from "@domain/organization/organization";
import { OrganizationApi } from "@domain/organization/organizationApi";
import { OrganizationService } from "@domain/organization/organizationService";
import { Profile } from "./profile";
import { DetailedPerson, Person } from "./user";
import { UserApi } from "./userApi";

export class UserService {
	profilesStore = new SimpleStore((id) => this.userApi.getProfile(id));
	slugProfilesStore = new SimpleStore<Profile>((slug) => this.userApi.getProfileBySlug(slug), { primaryKey: "slug" });
	followersStore = new SimpleMappedStorePresentation(this.profilesStore, (profileId, page) =>
		this.userApi.getFollowers(profileId, page)
	);
	followeesStore = new SimpleMappedStorePresentation(this.profilesStore, (profileId, page) =>
		this.userApi.getFollowees(profileId, page)
	);
	organizationTeachersStore = new SimpleMappedStorePresentation(this.profilesStore, (id, page) =>
		this.organizationApi.getMembers(id, page, ["CONTRIBUTOR"])
	);
	organizationAdminsStore = new SimpleMappedStorePresentation(this.profilesStore, (id, page) =>
		this.organizationApi.getMembers(id, page, ["ADMINISTRATOR", "SUPER_ADMINISTRATOR"])
	);
	groupMembersStore = new ContextStore(
		this.profilesStore,
		(id, page) => this.groupApi.getMembers(id, page, "MEMBER"),
		(groupId, userId) => this.groupApi.getMember(groupId, userId),
		(member) => {
			const { status, ...profile } = member;
			return profile;
		}
	);
	shareroomMembersStore = new ContextStore(
		this.profilesStore,
		(id) => this.groupApi.getShareroomLastFiveMembers(id),
		(groupId, userId) => this.groupApi.getMember(groupId, userId),
		(member) => {
			const { status, ...profile } = member;
			return profile;
		}
	);
	groupAdminsStore = new ContextStore(
		this.profilesStore,
		(id, page) => this.groupApi.getMembers(id, page, "ADMINISTRATOR"),
		(groupId, userId) => this.groupApi.getMember(groupId, userId),
		(member) => {
			const { status, ...profile } = member;
			return profile;
		}
	);
	groupPendingsStore = new ContextStore(
		this.profilesStore,
		(id, page) => this.groupApi.getMembers(id, page, "pending"),
		(groupId, userId) => this.groupApi.getMember(groupId, userId),
		(member) => {
			const { status, ...profile } = member;
			return profile;
		}
	);

	quizParticipantsStore = new SimpleMappedStorePresentation(this.profilesStore, (quizId: string, page) =>
		this.quizApi.getQuizzesParticipants(quizId, page)
	);

	constructor(
		private readonly userApi: UserApi,
		private readonly organizationApi: OrganizationApi,
		private readonly groupApi: GroupApi,
		private readonly currentProfileService: CurrentProfileService,
		private readonly organizationService: OrganizationService,
		private readonly groupService: GroupService,
		private readonly quizApi: QuizApi,
		initialProfiles?: Map<string, Profile>
	) {
		if (initialProfiles) {
			this.profilesStore.merge(List(initialProfiles.values()));
		}
		currentProfileService.currentProfile.onChange((p) => p && this.profilesStore.save(p));
		this.slugProfilesStore.bind(this.profilesStore);
	}

	async toggleFollow(person: DetailedPerson): Promise<void> {
		const isOrganization = person.type !== "USER";

		await this.followPerson(
			person,
			isOrganization ? [this.organizationService.organizationStore, this.profilesStore] : [this.profilesStore],
			isOrganization ? this.organizationApi : this.userApi
		);
	}

	private async followPerson(person: DetailedPerson, stores: Store<DetailedPerson>[], api: FollowApi): Promise<void> {
		if (!person.followedByMeSince) {
			await api.follow(person.id);
			stores.forEach((store) =>
				store.update(person.id, (p) => ({
					...p,
					followedByMeSince: new Date(),
					followerCount: p.followerCount + 1,
				}))
			);

			this.currentProfileService.update((p) => ({ ...p, followeeCount: p.followeeCount + 1 }));
		} else {
			await api.unfollow(person.id);
			stores.forEach((store) =>
				store.update(person.id, (p) => ({
					...p,
					followedByMeSince: undefined,
					followerCount: p.followerCount - 1,
				}))
			);
			this.currentProfileService.update((p) => ({ ...p, followeeCount: p.followeeCount - 1 }));
		}
	}

	async addMembersToOrganization(
		organizationId: string,
		members: List<string>,
		role: OrganizationMemberRole
	): Promise<void> {
		for (const member of members) {
			await this.organizationApi.addMember(organizationId, member, role);
		}
		const firstMember = members.first();
		if (role === "SUPER_ADMINISTRATOR" && firstMember) {
			await this.deleteMemberFromOrganization(organizationId, firstMember, "ADMINISTRATOR");
		}
		this.getOrganizationMemberStore(role).fetch(organizationId);
		this.organizationService.updateMemberCount(organizationId, role, members.size);
	}

	async deleteMemberFromOrganization(
		organizationId: string,
		memberId: string,
		role: OrganizationMemberRole
	): Promise<void> {
		await this.organizationApi.deleteMember(organizationId, memberId, role);
		this.getOrganizationMemberStore(role).fetch(organizationId);
		this.organizationService.updateMemberCount(organizationId, role, -1);
	}

	async inviteMembersToGroup(groupId: string, members: List<string>, role: GroupMemberRole): Promise<void> {
		for (const member of members) {
			await this.groupApi.inviteMember(groupId, member, role);
		}
		this.groupMembersStore.fetch(groupId);
	}
	async deleteMemberFromGroup(
		groupId: string,
		memberId: string,
		role: GroupMemberRole,
		pending?: boolean
	): Promise<void> {
		await this.groupApi.deleteMember(groupId, memberId, role);
		(pending ? this.groupPendingsStore : role === "MEMBER" ? this.groupMembersStore : this.groupAdminsStore).fetch(
			groupId
		);
		if (!pending) {
			this.groupService.updateMemberCount(groupId, role, -1);
		}
	}

	suggest = (count: number): Promise<Page<Profile>> => {
		return this.userApi.suggest(
			{ page: 0, size: count },
			this.currentProfileService.currentProfile.get()?.categories ?? []
		);
	};

	getOrganizationMemberStore(role: OrganizationMemberRole): SimpleMappedStorePresentation<Profile> {
		return role === "CONTRIBUTOR" ? this.organizationTeachersStore : this.organizationAdminsStore;
	}

	getGroupMemberStore(role: GroupMemberRole | "pending"): MappedStore<GroupMember, string> {
		return role === "pending"
			? this.groupPendingsStore
			: role === "MEMBER"
				? this.groupMembersStore
				: this.groupAdminsStore;
	}

	getShareroomLastFiveMembers(): MappedStore<GroupMember, string> {
		return this.shareroomMembersStore;
	}

	getMentions(query: string): Promise<Person[]> {
		return this.userApi.searchPersons(query, { page: 0, size: 5 });
	}

	search = this.userApi.search.bind(this.userApi);

	searchUsersWithExclusion = this.userApi.searchUsersWithExclusion.bind(this.userApi);

	searchUsersWithInclusion = this.userApi.searchUsersWithInclusion.bind(this.userApi);

	async disinterestUser(userId: string): Promise<void> {
		await this.userApi.disinterestUser(userId);
	}
}

interface FollowApi {
	follow: (id: string) => Promise<void>;
	unfollow: (id: string) => Promise<void>;
}
